import React from 'react'
import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Clock, Phone, Mail, AlertTriangle } from 'lucide-react'

export default function SubscriptionExpiredScreen() {
  const { user, logout } = useAuth()

  const handleContactAdmin = () => {
    // Open email client or show contact information
    window.location.href = 'mailto:admin@restaurant-analytics.com?subject=Subscription Renewal Request'
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 p-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center">
            <AlertTriangle className="h-16 w-16 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-red-600 dark:text-red-400">
            Subscription Expired
          </h1>
          <p className="text-muted-foreground">
            Your account access has been suspended due to expired subscription
          </p>
        </div>

        {/* Account Information */}
        <Card className="border-red-200 dark:border-red-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Account Details</CardTitle>
              <Badge variant="destructive">Expired</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Account Name</p>
                <p className="text-base">{user?.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-base">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Renewal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Renew Your Subscription</span>
            </CardTitle>
            <CardDescription>
              Contact our admin team to reactivate your account and continue using all features
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                Your account data is safe and will be restored once your subscription is renewed.
                Contact admin for immediate activation.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <h4 className="font-medium">Contact Information:</h4>
              <div className="grid gap-3">
                <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted">
                  <Mail className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="font-medium">Email Support</p>
                    <p className="text-sm text-muted-foreground">admin@restaurant-analytics.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted">
                  <Phone className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium">Phone Support</p>
                    <p className="text-sm text-muted-foreground">+91 98765 43210</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button 
            onClick={handleContactAdmin}
            className="flex-1"
            size="lg"
          >
            <Mail className="mr-2 h-4 w-4" />
            Contact Admin for Renewal
          </Button>
          <Button 
            variant="outline" 
            onClick={logout}
            size="lg"
          >
            Sign Out
          </Button>
        </div>

        {/* Feature List */}
        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="text-base">Features Available After Renewal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Real-time Analytics Dashboard</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Sales Reports & Insights</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Profit Analysis Tools</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Staff Performance Tracking</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Revenue Channel Analysis</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Export & Reporting Features</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}